import type { PropertyValueMap, PropertyValues } from 'lit';
import { css, html, LitElement, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import '../elements/d-wrap.js';
import '../elements/d-action.js';
import { DDialogContext } from 'src/outskirts/account/d-dialog-context.js';
import '../fields/d-closer.js';
import type { DialogActionInput } from './DialogActionInput';
import { IntersectionController } from '@lit-labs/observers/intersection-controller.js';

export abstract class BaseDialog<Input, Result> extends LitElement {
  static readonly styles = [
    css`
      :host {
        display: block;
      }

      dialog {
        container-name: dialog;
        container-type: inline-size;
      }

      dialog[open] {
        display: flex;
        flex-direction: column;
        outline: none;
        background: white;
        min-width: 356px;
        max-width: calc(100vw - 20px);
        max-height: calc((var(--vh, 1vh) * 100) - 140px);
        border: none;
        border-radius: 12px;
        padding: 0;
        box-shadow: 0 16px 24px 2px hsla(0, 0%, 0%, 0.3);
      }

      ::backdrop {
        position: fixed;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        width: 100%;
        height: 100%;
        opacity: 1;
        z-index: 1000;
        background-color: hsla(197, 82%, 20%, 0.6);
        backdrop-filter: blur(2px);
      }

      .closer {
        position: absolute;
        top: 10px;
        right: 10px;
        width: 20px;
        height: 20px;
      }

      .header {
        flex: none;
        display: flex;
        flex-direction: row-reverse;
        flex-wrap: wrap;
        justify-content: space-between;
        align-items: flex-end;
        padding: 16px 20px 12px 20px;
      }

      .title {
        flex-grow: 1;
      }

      .main-title {
        font-size: 22px;
        font-weight: bold;
      }

      .sub-title {
        font-size: 20px;
        font-weight: 400;
      }

      :host([employee-day]) .sub-title div:last-child {
        color: var(--holidayColor);
      }

      .header-actions {
        margin-right: -8px;
        padding: 0 0 6px 12px;
      }

      .body {
        position: relative;
        flex-grow: 1;
        padding: 0 20px;
        overflow-y: auto;
        outline: none;
      }

      :host([no-footer]) .body {
        padding-bottom: 12px;
      }

      #intersection-indicator-top {
        height: 1px;
        margin-bottom: -1px;
        pointer-events: none;
      }

      #intersection-indicator-bottom {
        height: 1px;
        margin-top: -1px;
        pointer-events: none;
      }

      .overflow-gradient {
        position: relative;
        padding: 0 20px;
        z-index: 2;
      }

      .overflow-gradient > div {
        position: absolute;
        display: none;
        justify-content: center;
        width: 100%;
        height: 20px;
        margin-left: -20px;
        z-index: 1;
        pointer-events: none;
      }

      .overflow-gradient.top > div {
        top: -1px;
        background: linear-gradient(180deg, hsla(0, 0%, 0%, 0.2) 0%, hsla(0, 0%, 0%, 0));
      }

      .top-out .overflow-gradient.top > div {
        display: flex;
      }

      .overflow-gradient.bottom > div {
        bottom: -1px;
        background: linear-gradient(180deg, hsla(0, 0%, 0%, 0) 0%, hsla(0, 0%, 0%, 0.2));
      }

      .bottom-out .overflow-gradient.bottom > div {
        display: flex;
      }

      .overflow-gradient > div > div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        margin-top: -20px;
        border-radius: 50%;
        background: white;
        box-shadow: 0 0 10px hsla(0, 0%, 0%, 0.3);
        pointer-events: all;
        cursor: pointer;
      }

      .overflow-gradient.top > div > div {
        margin-top: 10px;
        transform: rotate(180deg);
      }

      .overflow-gradient.bottom > div > div {
        margin-top: -20px;
      }

      .overflow-gradient > div > div:before {
        content: '';
        font-size: 15px;
        width: 0.8em;
        height: 0.8em;
        margin-top: -0.5em;
        border-left: 3px solid hsla(196, 83%, 50%, 1);
        border-bottom: 3px solid hsla(196, 83%, 50%, 1);
        transform: rotate(-45deg);
      }

      .footer {
        flex: none;
        min-height: 16px;
        padding: 0 20px;
      }

      .footer-actions {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        justify-content: flex-end;
        border-top: 1px solid var(--borderColor);
        padding: 16px 0 18px 0;
      }

      .footer-actions d-action + d-action[button] {
        margin-left: 8px;
      }

      @container dialog (width > 700px) {
        .header {
          padding: 20px 30px 12px 30px;
        }

        .body {
          padding: 0 30px;
        }

        .footer {
          padding: 0 30px;
        }

        .main-title {
          font-size: 32px;
        }

        .sub-title {
          font-size: 25px;
        }
      }
    `,
  ];
  private static stack = 0;
  @property({ type: Boolean })
  show = false;
  @property({ type: Number, attribute: 'width' })
  width = 600;
  @property({ type: String })
  headerIcon = '';
  @property({ type: String })
  title = '';
  @property({ type: String })
  subtitle = '';
  @property({ type: String })
  subtitleSecond = '';
  @property({ type: Boolean })
  cancelOnOutsideClick = false;
  @property({ type: Boolean })
  dark = false;
  @property({ type: Boolean })
  noHeader = false;
  @property({ type: Boolean, attribute: 'no-footer', reflect: true })
  noFooter = false;
  @property({ type: Array })
  headerActions: DialogActionInput[] = [];
  @property({ type: Array })
  footerActions: DialogActionInput[] = [];
  @query('#body')
  bodyElm!: HTMLElement;
  @query('dialog')
  private popupElement: HTMLDialogElement | undefined;
  private intersectionController = new IntersectionController(this, {
    callback: (entries) => this.onIntersectionChanged(entries),
    config: { threshold: [1, 0] },
  });

  protected get calculatedTitle(): string {
    return this.title;
  }

  protected get calculatedSubtitle(): string {
    return this.subtitle;
  }

  protected get calculatedHeaderActions(): DialogActionInput[] {
    return this.headerActions;
  }

  protected get calculatedFooterActions(): DialogActionInput[] {
    return this.footerActions;
  }

  protected get hideCloser() {
    return this.calculatedHeaderActions.length > 0 || this.calculatedFooterActions.length > 0;
  }

  private get cardWidthStyle() {
    return 'width: ' + this.width + 'px';
  }

  static async open<Dialog extends BaseDialog<Input, Result>, Input, Result>(
    this: { new (): Dialog },
    input: Input,
  ): Promise<Result> {
    return await BaseDialog.openDialog(this, input);
  }

  static async openDialog<Dialog extends BaseDialog<Input, Result>, Input, Result>(
    DialogCreator: { new (): Dialog },
    input: Input,
  ): Promise<Result> {
    const dialog = new DialogCreator();

    dialog.initializeDialog(input);
    DDialogContext.instance?.shadowRoot?.appendChild(dialog);

    await dialog.updateComplete;

    const resolver: Promise<Result> = new Promise<Result>((res) => {
      dialog.addEventListener<any>(
        'close',
        (e: CustomEvent<string>) => {
          e.stopPropagation();
          const result = dialog.fetchResult(dialog.popupElement?.returnValue);
          dialog.remove();
          res(result);
        },
        { once: true },
      );
    });

    await dialog.showModal();
    return resolver;
  }

  onIntersectionChanged(entries: IntersectionObserverEntry[]) {
    entries.forEach((e) => {
      if (e.target.id === 'intersection-indicator-top') {
        if (e.isIntersecting) {
          this.popupElement?.classList.remove('top-out');
        } else {
          this.popupElement?.classList.add('top-out');
        }
      }
      if (e.target.id === 'intersection-indicator-bottom') {
        if (e.isIntersecting) {
          this.popupElement?.classList.remove('bottom-out');
        } else {
          this.popupElement?.classList.add('bottom-out');
        }
      }
    });
  }

  scrollContent(direction: -1 | 1) {
    this.bodyElm.scrollBy({ left: 0, top: direction * this.bodyElm.offsetHeight, behavior: 'smooth' });
  }

  lock() {
    BaseDialog.stack = BaseDialog.stack + 1;
    const body = document.body;
    if (body.style.position !== 'fixed') {
      const html = document.documentElement;
      const w = Math.max(body.scrollWidth, body.offsetWidth, html.clientWidth, html.scrollWidth, html.offsetWidth);
      const h = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
      const x = window.scrollX;
      const y = window.scrollY;
      body.style.width = `${w}px`;
      body.style.height = `${h}px`;
      body.style.left = `-${x}px`;
      body.style.top = `-${y}px`;
      body.style.position = 'fixed';
    }
  }

  unlock() {
    BaseDialog.stack = BaseDialog.stack - 1;
    if (!BaseDialog.stack) {
      const body = document.body;
      const x = body.style.left;
      const y = body.style.top;
      body.style.removeProperty('width');
      body.style.removeProperty('height');
      body.style.removeProperty('left');
      body.style.removeProperty('top');
      body.style.removeProperty('position');
      body.style.top = '';
      window.scrollTo(parseInt(x || '0') * -1, parseInt(y || '0') * -1);
    }
  }

  onClick(event) {
    if (
      this.shadowRoot &&
      this.popupElement &&
      event.target === this.popupElement &&
      (this.cancelOnOutsideClick || !this.hideCloser)
    ) {
      this.popupElement.close();
    }
  }

  onClose(e) {
    this.show = false;
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true, detail: e.detail }));
    this.unlock();
  }

  async showModal() {
    await this.updateComplete;
    if (this.popupElement) {
      this.popupElement.showModal();
      this.lock();
    }
  }

  render() {
    return html`
      <dialog id="popup" style="${this.cardWidthStyle}" @click=${this.onClick} @close=${this.onClose}>
        ${this.hideCloser
          ? nothing
          : html`<d-closer
              @click=${() => {
                this.close();
              }}
            ></d-closer> `}
        ${!this.noHeader ? html` <div class="header">${this.renderHeader()}</div> ` : nothing}
        ${this.renderPostHeader()}
        <div class="overflow-gradient top">
          <div>
            <div @mousedown=${() => this.scrollContent(-1)}></div>
          </div>
        </div>
        <div class="body" id="body">
          <div id="intersection-indicator-top"></div>
          ${this.renderBody()}
          <div id="intersection-indicator-bottom"></div>
        </div>
        <div class="overflow-gradient bottom">
          <div>
            <div @mousedown=${() => this.scrollContent(1)}></div>
          </div>
        </div>
        ${!this.noFooter ? html` <div class="footer">${this.renderFooter()}</div> ` : nothing}
      </dialog>
    `;
  }

  toggleShow() {
    if (this.shadowRoot && this.popupElement) {
      if (this.show) {
        this.popupElement.showModal();
      } else {
        this.popupElement.close();
      }
    }
  }

  close() {
    if (this.shadowRoot) {
      const popup = this.shadowRoot.getElementById('popup') as HTMLDialogElement;
      if (popup) {
        popup.close();
      }
    }
  }

  protected async onDispatchAction(disabled: boolean, action: string) {
    if (!disabled) {
      this.popupElement?.close(action);
    }
  }

  protected abstract fetchResult(detail: string | undefined): Result;

  protected abstract initializeDialog(input: Input): void;

  protected renderHeader() {
    return html`
      ${this.calculatedHeaderActions.length > 0
        ? html`
            <div class="header-actions">
              ${this.calculatedHeaderActions.map((a) => {
                return html` <d-action
                  @click=${async () => this.onDispatchAction(a.disabled ?? false, a.action)}
                  ?disabled=${a.disabled}
                  ?delete=${a.action === 'delete'}
                  >${a.name}</d-action
                >`;
              })}
            </div>
          `
        : nothing}
      <d-wrap class="title" baseline tight>
        ${this.calculatedTitle ? html` <div class="main-title">${this.calculatedTitle}</div>` : nothing}
        <d-wrap baseline tight class="sub-title">
          <div>${this.calculatedSubtitle}</div>
          <div>${this.subtitleSecond}</div>
        </d-wrap>
      </d-wrap>
    `;
  }

  protected renderPostHeader() {
    return html``;
  }

  protected renderBody() {
    return html`<slot></slot>`;
  }

  protected renderFooterActions() {
    return html`
      ${this.calculatedFooterActions.map((a) => {
        return html` <d-action
          @click=${async () => this.onDispatchAction(a.disabled ?? false, a.action ?? '')}
          plain
          ?disabled=${a.disabled}
          ?button=${a.main}
          ?dark=${this.dark}
          ?delete=${a.action === 'delete'}
          >${a.name}</d-action
        >`;
      })}
    `;
  }

  protected renderFooter() {
    return html`
      ${this.calculatedFooterActions.length > 0
        ? html` <div class="footer-actions">${this.renderFooterActions()}</div> `
        : nothing}
    `;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    const topIndicator = this.shadowRoot?.getElementById('intersection-indicator-top');
    if (topIndicator) {
      this.intersectionController.observe(topIndicator);
    }
    const bottomIndicator = this.shadowRoot?.getElementById('intersection-indicator-bottom');
    if (bottomIndicator) {
      this.intersectionController.observe(bottomIndicator);
    }
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('show')) {
      this.toggleShow();
      if (this.bodyElm) {
        setTimeout(() => {
          this.bodyElm.scrollTop = 0;
        }, 0);
      }
    }
  }
}
